import { useState } from 'react'
import { Compass, Search, Clock, Zap, Plus, Trash2, ToggleLeft, ToggleRight, Layers, ChevronDown, ChevronUp, X, BarChart3, Gauge, TrendingUp } from 'lucide-react'
import { useApi } from '../../hooks/useApi'
import { apiPost, apiDelete } from '../../lib/auth'

interface DiscoveryStats {
  totalTopics: number
  activeTopics: number
  exploredTopics: number
  unexploredTopics: number
  totalDiscovered: number
  lastRun: string | null
  lastRunDetails: { topicsScanned?: number; keywordsFound?: number } | null
}

interface Topic {
  id: number
  topic: string
  category: string
  lastExploredAt: string | null
  keywordsFound: number
  estimatedPotential: number | null
  explorationPercent: number | null
  active: boolean
  createdAt: string
}

interface TopicKeyword {
  id: number
  keyword: string
  volume: number | null
  cpc: number | null
  kdScore: number | null
  lastSerpUpdate: string | null
  lastVolumeUpdate: string | null
}

interface TopicKeywordsData {
  topic: string
  category: string
  totalKeywords: number
  totalVolume: number
  avgKd: number | null
  keywords: TopicKeyword[]
}

function kdColor(kd: number | null): string {
  if (kd == null) return '#8B8FA7'
  if (kd < 30) return '#10B981'
  if (kd < 60) return '#FBBF24'
  return '#EF4444'
}

function formatVolume(vol: number | null): string {
  if (vol == null) return '-'
  if (vol >= 1000000) return (vol / 1000000).toFixed(1) + 'M'
  if (vol >= 1000) return (vol / 1000).toFixed(1) + 'K'
  return vol.toLocaleString('fr-FR')
}

const CATEGORIES = [
  { value: 'artisans_batiment', label: 'Artisans & Batiment' },
  { value: 'professions_liberales', label: 'Professions liberales' },
  { value: 'sante', label: 'Sante' },
  { value: 'commerce_local', label: 'Commerce local' },
  { value: 'immobilier', label: 'Immobilier' },
  { value: 'finance_assurance', label: 'Finance & Assurance' },
  { value: 'digital_tech', label: 'Digital & Tech' },
  { value: 'formation_education', label: 'Formation & Education' },
  { value: 'services_entreprise', label: 'Services entreprise' },
  { value: 'tourisme_loisirs', label: 'Tourisme & Loisirs' },
]

function categoryLabel(value: string): string {
  return CATEGORIES.find((c) => c.value === value)?.label ?? value
}

function relativeDate(dateStr: string | null): string {
  if (!dateStr) return 'Jamais'
  const diff = Date.now() - new Date(dateStr).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return "A l'instant"
  if (mins < 60) return `Il y a ${mins}min`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `Il y a ${hours}h`
  const days = Math.floor(hours / 24)
  if (days < 7) return `Il y a ${days}j`
  return new Date(dateStr).toLocaleDateString('fr-FR')
}

function explorationColor(pct: number | null): string {
  if (pct == null || pct < 25) return '#8B8FA7'
  if (pct < 75) return '#FBBF24'
  return '#10B981'
}

function Skeleton() {
  return <div className="animate-pulse bg-[#E2E4ED] rounded h-8 w-20" />
}

export default function GlobalDbDiscovery() {
  const { data: stats, loading: statsLoading, refetch: refetchStats } = useApi<DiscoveryStats>('/api/admin/global-db/discovery')
  const { data: topics, loading: topicsLoading, refetch: refetchTopics } = useApi<Topic[]>('/api/admin/global-db/discovery/topics')

  const [newTopic, setNewTopic] = useState('')
  const [newCategory, setNewCategory] = useState(CATEGORIES[0].value)
  const [adding, setAdding] = useState(false)
  const [deletingId, setDeletingId] = useState<number | null>(null)
  const [togglingId, setTogglingId] = useState<number | null>(null)
  const [expandedTopicId, setExpandedTopicId] = useState<number | null>(null)
  const [topicKeywords, setTopicKeywords] = useState<TopicKeywordsData | null>(null)
  const [loadingKeywords, setLoadingKeywords] = useState(false)

  const refetchAll = () => {
    refetchStats()
    refetchTopics()
  }

  const handleAdd = async () => {
    if (!newTopic.trim()) return
    setAdding(true)
    const res = await apiPost('/api/admin/global-db/discovery/topics', { topic: newTopic.trim(), category: newCategory })
    setAdding(false)
    if (!res.error) {
      setNewTopic('')
      refetchAll()
    }
  }

  const handleToggle = async (topic: Topic) => {
    setTogglingId(topic.id)
    await fetch(`/api/admin/global-db/discovery/topics/${topic.id}`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ active: !topic.active }),
    })
    setTogglingId(null)
    refetchAll()
  }

  const handleExpand = async (topicId: number) => {
    if (expandedTopicId === topicId) {
      setExpandedTopicId(null)
      setTopicKeywords(null)
      return
    }
    setExpandedTopicId(topicId)
    setLoadingKeywords(true)
    try {
      const res = await fetch(`/api/admin/global-db/discovery/topics/${topicId}/keywords?limit=200`, { credentials: 'include' })
      const json = await res.json()
      if (json.success) setTopicKeywords(json.data)
    } catch {}
    setLoadingKeywords(false)
  }

  const handleDelete = async (id: number) => {
    if (!confirm('Supprimer ce topic ?')) return
    setDeletingId(id)
    await apiDelete(`/api/admin/global-db/discovery/topics/${id}`)
    setDeletingId(null)
    refetchAll()
  }

  // Group topics by category
  const grouped = (topics ?? []).reduce<Record<string, Topic[]>>((acc, t) => {
    ;(acc[t.category] ??= []).push(t)
    return acc
  }, {})

  const kpis = [
    { label: 'Total topics', value: stats?.totalTopics, icon: Layers, color: '#6366F1' },
    { label: 'Explores', value: stats?.exploredTopics, icon: Search, color: '#10B981' },
    { label: 'En attente', value: stats?.unexploredTopics, icon: Clock, color: '#FBBF24' },
    { label: 'KW decouverts', value: stats?.totalDiscovered, icon: Zap, color: '#A855F7' },
  ]

  return (
    <div className="space-y-6">
      {/* Section 1 — Stats */}
      <div className="bg-white rounded-xl border border-[#E2E4ED] p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Compass size={20} strokeWidth={1.25} className="text-[#6366F1]" />
            <h2 className="text-lg font-semibold text-[#1B2559]">Discovery</h2>
          </div>
          {stats?.lastRun && (
            <span className="text-xs text-[#8B8FA7]">
              Dernier run : {relativeDate(stats.lastRun)}
              {stats.lastRunDetails && (
                <> — {stats.lastRunDetails.topicsScanned} topics, {stats.lastRunDetails.keywordsFound} KW</>
              )}
            </span>
          )}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {kpis.map((kpi) => (
            <div key={kpi.label} className="flex items-start gap-3">
              <div className="p-2 rounded-lg shrink-0" style={{ backgroundColor: `${kpi.color}14` }}>
                <kpi.icon size={18} strokeWidth={1.25} style={{ color: kpi.color }} />
              </div>
              <div>
                {statsLoading ? (
                  <Skeleton />
                ) : (
                  <p className="text-2xl font-bold text-[#1B2559]">{kpi.value?.toLocaleString('fr-FR') ?? '-'}</p>
                )}
                <p className="text-sm text-[#8B8FA7]">{kpi.label}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Section 2 — Add topic */}
      <div className="bg-white rounded-xl border border-[#E2E4ED] p-5">
        <h3 className="text-sm font-semibold text-[#1B2559] mb-3">Ajouter un topic</h3>
        <div className="flex flex-col sm:flex-row gap-3">
          <input
            type="text"
            value={newTopic}
            onChange={(e) => setNewTopic(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
            placeholder="Ex: plombier, osteopathe, boulangerie..."
            className="flex-1 px-3 py-2 rounded-lg border border-[#E2E4ED] text-sm text-[#1B2559] placeholder:text-[#8B8FA7] focus:outline-none focus:ring-2 focus:ring-[#6366F1]/30 focus:border-[#6366F1]"
          />
          <select
            value={newCategory}
            onChange={(e) => setNewCategory(e.target.value)}
            className="px-3 py-2 rounded-lg border border-[#E2E4ED] text-sm text-[#1B2559] focus:outline-none focus:ring-2 focus:ring-[#6366F1]/30 focus:border-[#6366F1]"
          >
            {CATEGORIES.map((c) => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
          <button
            onClick={handleAdd}
            disabled={adding || !newTopic.trim()}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium text-white bg-[#6366F1] hover:bg-[#4F46E5] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Plus size={16} strokeWidth={1.25} />
            {adding ? 'Ajout...' : 'Ajouter'}
          </button>
        </div>
      </div>

      {/* Section 3 — Topics list grouped by category */}
      <div className="bg-white rounded-xl border border-[#E2E4ED] p-5">
        <h3 className="text-sm font-semibold text-[#1B2559] mb-4">Topics par categorie</h3>

        {topicsLoading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="animate-pulse bg-[#E2E4ED] rounded h-12 w-full" />
            ))}
          </div>
        ) : Object.keys(grouped).length === 0 ? (
          <p className="text-sm text-[#8B8FA7] text-center py-8">Aucun topic pour le moment</p>
        ) : (
          <div className="space-y-6">
            {Object.entries(grouped)
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([category, catTopics]) => (
                <div key={category}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs font-semibold text-[#6366F1] uppercase tracking-wide">
                      {categoryLabel(category)}
                    </span>
                    <span className="text-xs text-[#8B8FA7]">({catTopics.length})</span>
                  </div>
                  <div className="space-y-2">
                    {catTopics.map((topic) => {
                      const pct = topic.explorationPercent
                      const barColor = explorationColor(pct)
                      const isExpanded = expandedTopicId === topic.id
                      return (
                        <div key={topic.id}>
                          <div
                            className={`flex items-center gap-4 px-4 py-3 rounded-lg border transition-colors cursor-pointer ${
                              topic.active
                                ? isExpanded
                                  ? 'border-[#6366F1] bg-[#6366F1]/5'
                                  : 'border-[#E2E4ED] bg-[#F8F9FC] hover:border-[#C8CBD8]'
                                : 'border-[#E2E4ED] bg-white opacity-60'
                            }`}
                            onClick={() => handleExpand(topic.id)}
                          >
                            {/* Expand icon */}
                            <span className="text-[#8B8FA7]">
                              {isExpanded ? <ChevronUp size={16} strokeWidth={1.25} /> : <ChevronDown size={16} strokeWidth={1.25} />}
                            </span>

                            {/* Name */}
                            <span className="text-sm font-medium text-[#1B2559] min-w-[140px]">
                              {topic.topic}
                            </span>

                            {/* KW found */}
                            <span className="text-xs text-[#8B8FA7] whitespace-nowrap">
                              {topic.keywordsFound.toLocaleString('fr-FR')} KW
                            </span>

                            {/* Progress bar */}
                            <div className="flex-1 flex items-center gap-2 min-w-[120px]">
                              <div className="flex-1 h-2 rounded-full bg-[#E2E4ED] overflow-hidden">
                                <div
                                  className="h-full rounded-full transition-all"
                                  style={{
                                    width: `${pct ?? 0}%`,
                                    backgroundColor: barColor,
                                  }}
                                />
                              </div>
                              <span className="text-xs text-[#8B8FA7] w-10 text-right">
                                {pct != null ? `${pct}%` : '-'}
                              </span>
                            </div>

                            {/* Last scan */}
                            <span className="text-xs text-[#8B8FA7] whitespace-nowrap min-w-[80px] text-right">
                              {relativeDate(topic.lastExploredAt)}
                            </span>

                            {/* Toggle active */}
                            <button
                              onClick={(e) => { e.stopPropagation(); handleToggle(topic) }}
                              disabled={togglingId === topic.id}
                              className="text-[#8B8FA7] hover:text-[#6366F1] transition-colors disabled:opacity-50"
                              title={topic.active ? 'Desactiver' : 'Activer'}
                            >
                              {topic.active ? (
                                <ToggleRight size={22} strokeWidth={1.25} className="text-[#10B981]" />
                              ) : (
                                <ToggleLeft size={22} strokeWidth={1.25} />
                              )}
                            </button>

                            {/* Delete */}
                            <button
                              onClick={(e) => { e.stopPropagation(); handleDelete(topic.id) }}
                              disabled={deletingId === topic.id}
                              className="text-[#8B8FA7] hover:text-[#EF4444] transition-colors disabled:opacity-50"
                              title="Supprimer"
                            >
                              <Trash2 size={16} strokeWidth={1.25} />
                            </button>
                          </div>

                          {/* Expanded keywords panel */}
                          {isExpanded && (
                            <div className="mt-1 ml-8 mr-2 border border-[#E2E4ED] rounded-lg bg-white overflow-hidden">
                              {loadingKeywords ? (
                                <div className="p-4 space-y-2">
                                  {[1, 2, 3].map(i => <div key={i} className="animate-pulse bg-[#E2E4ED] rounded h-6 w-full" />)}
                                </div>
                              ) : topicKeywords ? (
                                <>
                                  {/* Summary bar */}
                                  <div className="flex items-center gap-6 px-4 py-3 bg-[#F8F9FC] border-b border-[#E2E4ED]">
                                    <div className="flex items-center gap-1.5">
                                      <BarChart3 size={14} strokeWidth={1.25} className="text-[#6366F1]" />
                                      <span className="text-xs text-[#8B8FA7]">Volume total</span>
                                      <span className="text-sm font-semibold text-[#1B2559]">{formatVolume(topicKeywords.totalVolume)}</span>
                                    </div>
                                    <div className="flex items-center gap-1.5">
                                      <Gauge size={14} strokeWidth={1.25} className="text-[#A855F7]" />
                                      <span className="text-xs text-[#8B8FA7]">KD moyen</span>
                                      <span className="text-sm font-semibold" style={{ color: kdColor(topicKeywords.avgKd) }}>
                                        {topicKeywords.avgKd ?? '-'}
                                      </span>
                                    </div>
                                    <div className="flex items-center gap-1.5">
                                      <TrendingUp size={14} strokeWidth={1.25} className="text-[#10B981]" />
                                      <span className="text-xs text-[#8B8FA7]">Mots-cles</span>
                                      <span className="text-sm font-semibold text-[#1B2559]">{topicKeywords.totalKeywords}</span>
                                    </div>
                                  </div>

                                  {/* Keywords table */}
                                  <div className="max-h-80 overflow-y-auto">
                                    <table className="w-full text-sm">
                                      <thead className="sticky top-0 bg-white border-b border-[#E2E4ED]">
                                        <tr className="text-xs text-[#8B8FA7]">
                                          <th className="text-left px-4 py-2 font-medium">Mot-cle</th>
                                          <th className="text-right px-4 py-2 font-medium">Volume</th>
                                          <th className="text-right px-4 py-2 font-medium">CPC</th>
                                          <th className="text-right px-4 py-2 font-medium">KD</th>
                                          <th className="text-right px-4 py-2 font-medium">SERP</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {topicKeywords.keywords.map((kw) => (
                                          <tr key={kw.id} className="border-b border-[#E2E4ED]/50 hover:bg-[#F8F9FC] transition-colors">
                                            <td className="px-4 py-2 text-[#1B2559]">{kw.keyword}</td>
                                            <td className="px-4 py-2 text-right text-[#1B2559] font-medium">{formatVolume(kw.volume)}</td>
                                            <td className="px-4 py-2 text-right text-[#8B8FA7]">{kw.cpc != null ? `${kw.cpc.toFixed(2)}€` : '-'}</td>
                                            <td className="px-4 py-2 text-right">
                                              <span className="font-medium" style={{ color: kdColor(kw.kdScore) }}>
                                                {kw.kdScore ?? '-'}
                                              </span>
                                            </td>
                                            <td className="px-4 py-2 text-right text-[#8B8FA7] text-xs">
                                              {kw.lastSerpUpdate ? relativeDate(kw.lastSerpUpdate) : 'Pas de SERP'}
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>

                                  {topicKeywords.keywords.length === 0 && (
                                    <p className="text-sm text-[#8B8FA7] text-center py-6">Aucun mot-cle trouve pour ce topic</p>
                                  )}
                                </>
                              ) : null}
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>
    </div>
  )
}
